import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  Inbox,
  Mail,
  Building2,
  Calendar,
  Search,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LogoLockup } from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ccg_admin_token";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

/* ------- Login view ------- */
const LoginView = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Enter the admin password.");
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post(`${API}/admin/login`, { password });
      const token = res.data?.token;
      if (!token) throw new Error("No token");
      localStorage.setItem(TOKEN_KEY, token);
      onSuccess(token);
    } catch (err) {
      const msg =
        err?.response?.status === 401
          ? "Wrong password."
          : "Login failed. Try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 grid place-items-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid mask-fade-b opacity-60 pointer-events-none" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 h-[420px] w-[620px] bg-cyan-500/10 blur-3xl pointer-events-none" />

      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="relative w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900/80 backdrop-blur p-8 md:p-10 shadow-cyan-glow"
      >
        <div className="flex items-center justify-center mb-7">
          <LogoLockup size={48} />
        </div>
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 mb-3 text-center">
          Admin access
        </div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-50 tracking-tight text-center mb-2">
          Submissions dashboard
        </h1>
        <p className="text-slate-400 text-sm text-center mb-8">
          Enter the shared admin password to continue.
        </p>

        <label className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 block">
          Password
        </label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            data-testid="admin-password-input"
            className="pl-10 bg-slate-950 border-slate-800 h-11 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400 focus-visible:ring-offset-0 focus-visible:border-cyan-400"
            autoFocus
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full mt-6 h-11 rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold text-sm disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign in"}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>

        <p className="mt-6 text-center text-xs text-slate-500">
          <a href="/" className="hover:text-cyan-300">
            ← Back to site
          </a>
        </p>
      </form>
    </div>
  );
};

/* ------- Dashboard view ------- */
const Dashboard = ({ token, onLogout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/submissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired — please sign in again.");
        onLogout();
        return;
      }
      toast.error("Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter(
      (i) =>
        i.name?.toLowerCase().includes(q) ||
        i.email?.toLowerCase().includes(q) ||
        i.company?.toLowerCase().includes(q) ||
        i.details?.toLowerCase().includes(q)
    );
  }, [items, query]);

  const thisWeek = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return items.filter((i) => new Date(i.created_at).getTime() >= cutoff)
      .length;
  }, [items]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-3">
            <span className="hidden sm:block">
              <LogoLockup size={36} />
            </span>
            <span className="sm:hidden">
              <LogoLockup size={28} />
            </span>
          </a>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={load}
              disabled={loading}
              data-testid="admin-refresh"
              className="text-slate-300 hover:text-cyan-300 hover:bg-slate-900"
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
            <Button
              variant="ghost"
              onClick={onLogout}
              data-testid="admin-logout"
              className="text-slate-300 hover:text-red-300 hover:bg-slate-900"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Title + stats */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 mb-2">
              Inbox
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">
              Project submissions
            </h1>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="px-5 py-4 rounded-xl border border-slate-800 bg-slate-900/60">
              <div className="text-xs text-slate-500 font-mono uppercase tracking-wider">
                Total
              </div>
              <div className="font-display text-2xl font-bold text-slate-50 mt-1">
                {items.length}
              </div>
            </div>
            <div className="px-5 py-4 rounded-xl border border-slate-800 bg-slate-900/60">
              <div className="text-xs text-slate-500 font-mono uppercase tracking-wider">
                Last 7 days
              </div>
              <div className="font-display text-2xl font-bold text-cyan-300 mt-1">
                {thisWeek}
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, company, or details…"
            data-testid="admin-search"
            className="pl-10 bg-slate-900 border-slate-800 h-11 text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-400 focus-visible:ring-offset-0 focus-visible:border-cyan-400"
          />
        </div>

        {/* List */}
        <div
          data-testid="admin-submissions-list"
          className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden"
        >
          {loading && items.length === 0 ? (
            <div className="p-16 text-center text-slate-500">
              Loading submissions…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center">
              <Inbox className="h-10 w-10 text-slate-700 mx-auto mb-3" />
              <div className="text-slate-300 font-medium">
                {items.length === 0
                  ? "No submissions yet."
                  : "No matches for your search."}
              </div>
              <div className="text-slate-500 text-sm mt-1">
                {items.length === 0 &&
                  "New project briefs will appear here the moment they come in."}
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-slate-800">
              {filtered.map((sub) => (
                <li
                  key={sub.id}
                  className="group grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-5 md:px-8 py-5 hover:bg-slate-900/60 transition-colors cursor-pointer"
                  onClick={() => setSelected(sub)}
                  data-testid={`submission-row-${sub.id}`}
                >
                  <div className="md:col-span-3">
                    <div className="font-semibold text-slate-100">
                      {sub.name}
                    </div>
                    <a
                      href={`mailto:${sub.email}`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1.5 text-sm text-cyan-300 hover:text-cyan-200 mt-0.5"
                    >
                      <Mail className="h-3 w-3" />
                      {sub.email}
                    </a>
                  </div>
                  <div className="md:col-span-3 text-sm text-slate-300 flex items-center gap-2">
                    <Building2 className="h-3.5 w-3.5 text-slate-500" />
                    {sub.company || <span className="text-slate-600">—</span>}
                  </div>
                  <div className="md:col-span-3 text-sm text-slate-400 line-clamp-2">
                    {sub.details}
                  </div>
                  <div className="md:col-span-2 text-xs font-mono text-slate-500 flex items-center gap-1.5 md:justify-end">
                    <Calendar className="h-3 w-3" />
                    {formatDate(sub.created_at)}
                  </div>
                  <div className="md:col-span-1 flex md:justify-end items-center">
                    {sub.project_type && (
                      <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border border-slate-800 text-slate-400 group-hover:border-cyan-400/30 group-hover:text-cyan-300">
                        {sub.project_type}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      {/* Detail drawer */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="submission-detail-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl rounded-2xl border border-slate-800 bg-slate-900 p-6 md:p-8 max-h-[85vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2">
                  Submission · {selected.id.slice(0, 8)}
                </div>
                <h2 className="font-display text-2xl font-bold tracking-tight">
                  {selected.name}
                </h2>
                <a
                  href={`mailto:${selected.email}`}
                  className="text-cyan-300 hover:text-cyan-200 text-sm"
                >
                  {selected.email}
                </a>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-slate-400 hover:text-slate-100 text-sm"
              >
                Close
              </button>
            </div>

            <dl className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <dt className="text-xs font-mono uppercase tracking-wider text-slate-500">
                  Company
                </dt>
                <dd className="text-slate-100 mt-1">
                  {selected.company || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-mono uppercase tracking-wider text-slate-500">
                  Received
                </dt>
                <dd className="text-slate-100 mt-1">
                  {formatDate(selected.created_at)}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-mono uppercase tracking-wider text-slate-500">
                  Project type
                </dt>
                <dd className="text-slate-100 mt-1">
                  {selected.project_type || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-mono uppercase tracking-wider text-slate-500">
                  Budget
                </dt>
                <dd className="text-slate-100 mt-1">
                  {selected.budget || "—"}
                </dd>
              </div>
            </dl>

            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">
                Project details
              </div>
              <div className="rounded-xl border border-slate-800 bg-slate-950 p-5 text-slate-200 text-sm whitespace-pre-wrap leading-relaxed">
                {selected.details}
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <a
                href={`mailto:${selected.email}?subject=Re: Your project inquiry`}
                className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-semibold text-sm transition-colors"
              >
                Reply via email <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ------- Root ------- */
const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) {
      axios
        .post(
          `${API}/admin/logout`,
          {},
          { headers: { Authorization: `Bearer ${t}` } }
        )
        .catch(() => {});
    }
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginView onSuccess={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
};

export default Admin;
